import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Badge from '@amzn/awsui-components-core/badge';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { AgGridReact } from 'ag-grid-react';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { DatasetWizard } from 'src/components/star-grid/datasets/components/DatasetWizard';
import { useDatasets } from 'src/components/star-grid/datasets/hooks/useDatasets';
import { useDatasetGrid, type DatasetActionParams } from 'src/components/star-grid/datasets/hooks/useDatasetGrid';
import type { Dataset } from 'src/components/star-grid/datasets/datasetTypes';
import styles from 'src/components/star-grid/views/styles/SnapshotManagerView.module.scss';
import 'src/components/star-grid/views/styles/snapshot-manager-grid.scss';

type WizardTarget = { readonly editingId?: string } | null;

/** Admin: the onboarded datasets, each editable, and the wizard to onboard a new one. */
export const OnboardDatasetView: React.FC = () => {
  const text = useStarGridText();
  const [wizard, setWizard] = React.useState<WizardTarget>(null);
  const [saved, setSaved] = React.useState<Dataset | null>(null);
  const list = useDatasets(false);
  const components = React.useMemo(
    () => ({
      datasetActions: ({ data }: DatasetActionParams) =>
        data ? (
          <Button variant="inline-link" data-testid="dataset-edit" onClick={() => setWizard({ editingId: data.id })}>
            {text('datasetsEdit')}
          </Button>
        ) : null
    }),
    [text]
  );
  const grid = useDatasetGrid(text, { cellRenderer: 'datasetActions' });

  const onSaved = (dataset: Dataset) => {
    setSaved(dataset);
    list.reload();
  };

  return (
    <div className={styles.page} data-testid="onboard-dataset">
      <header className={styles.header}>
        <div>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{text('datasetsTitle')}</h1>
            <Badge color="grey">{text('datasetsUnderDevelopment')}</Badge>
          </div>
          <p className={styles.subtitle}>{text('onboardDatasetHelp')}</p>
        </div>
        <Button variant="primary" iconName="add-plus" data-testid="onboard-dataset-start" onClick={() => setWizard({})}>
          {text('onboardDatasetStart')}
        </Button>
      </header>
      <SpaceBetween size="s">
        <Alert type="info" data-testid="onboard-dataset-visibility-note">
          {text('datasetVisibilityUpcoming')}
        </Alert>
        {saved && (
          <Alert type="success" data-testid="onboard-dataset-saved">
            {text('onboardDatasetSaved')}: {saved.name}
          </Alert>
        )}
        {list.error && <Alert type="error">{list.error}</Alert>}
        <div className={styles.grid} data-testid="dataset-list">
          <AgGridReact<Dataset>
            theme={grid.theme}
            rowData={list.datasets}
            columnDefs={grid.columns}
            components={components}
            defaultColDef={{ sortable: true, resizable: true, suppressMovable: true, filter: true }}
            getRowId={({ data }) => data.id}
            loading={list.loading}
            overlayNoRowsTemplate={text('datasetsEmpty')}
            rowHeight={34}
            headerHeight={36}
            tooltipShowDelay={300}
          />
        </div>
      </SpaceBetween>
      {wizard && <DatasetWizard text={text} visible editingId={wizard.editingId} onDismiss={() => setWizard(null)} onSaved={onSaved} />}
    </div>
  );
};
